import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSettings } from '../settings/SettingsContext';
import { useT } from '../i18n/i18n';
import { programPhrases } from '../games/phrases';
import { readySpeech, serverVoice, setDeviceVoiceForSession } from '../games/speak';
import './speechGate.css';

const SLOW_AFTER_MS = 30_000;
const SHOW_AFTER_MS = 500;

export default function SpeechGate({ kind, config, children }: { kind: string; config: Record<string, unknown>; children: ReactNode }) {
  const t = useT();
  const { settings } = useSettings();
  const phrases = useMemo(() => programPhrases(kind, config), [kind, config]);
  const needsServer = serverVoice(settings.ttsVoice) !== null;
  const [open, setOpen] = useState(!needsServer || phrases.length === 0);
  const [progress, setProgress] = useState({ ready: 0, total: phrases.length });
  const [slow, setSlow] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) return;
    let wanted = true;
    const slowTimer = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    const showTimer = window.setTimeout(() => setShown(true), SHOW_AFTER_MS);
    void readySpeech(phrases, settings.ttsVoice, settings.ttsRate, (ready, total) => setProgress({ ready, total }), () => wanted)
      .then(() => { if (wanted) setOpen(true); });
    return () => { wanted = false; window.clearTimeout(slowTimer); window.clearTimeout(showTimer); };
  }, [open, phrases, settings.ttsVoice, settings.ttsRate]);

  useEffect(() => () => setDeviceVoiceForSession(false), []);

  if (open) return <>{children}</>;
  if (!shown) return null;

  return (
    <section className="card speech-gate" aria-labelledby="speech-gate-h">
      <h2 id="speech-gate-h">{t('Getting the voice ready')}</h2>
      <p role="status">{t('{ready} of {total} ready', { ready: progress.ready, total: progress.total })}</p>
      <progress max={progress.total || 1} value={progress.ready} aria-label={t('Getting the voice ready')} />
      {slow && (
        <>
          <p>{t('This is taking longer than usual. You can keep waiting, or start now with this device\'s own voice for the whole game.')}</p>
          <div className="toolbar">
            <button type="button" className="btn secondary" onClick={() => { setDeviceVoiceForSession(true); setOpen(true); }}>
              {t('Start with this device\'s voice')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
